import {Stack} from '@mui/material'
import React, {useEffect, useRef, useState} from 'react'
import {colors} from '@/constants'
import Select from '@/components/UI/select'
import useFetching from '@/hooks/useFetching'
import UserService from '@/services/api/User'
import {TRANSACTION_CONTENT_ENUM, TRANSACTION_TYPE_ENUM} from '@/types/enums'
import {TransactionInterface} from '@/types/types'
import {
  getTransactionContent,
  getTransactionType,
} from '@/types/model/transaction'
import SimpleLoader from '@/components/UI/simpleLoader'
import TransactionsList from '@/components/Modals/Profile/Transactions/TransactionsList'
import TransCustom from '@/components/UI/i18next'
import Report from '@/components/Modals/Profile/Transactions/Report'

const LIST_TAB = 0
const REPORT_TAB = 1

const Tabs = ({tab, setTab}: {tab: number; setTab: (v: number) => void}) => (
  <Stack direction="row" gap="10px" marginTop="24px">
    {[
      {key: LIST_TAB, i18nKey: '1687'},
      {key: REPORT_TAB, i18nKey: '1688'},
    ].map((item) => (
      <Stack
        key={item.key}
        className="pointer"
        onClick={() => setTab(item.key)}
        bgcolor={tab === item.key ? colors.orange : '#73716D66'}
        borderRadius="10px"
        padding="7px 18px">
        <TransCustom
          i18nKey={item.i18nKey}
          variant="body2"
          color={tab === item.key ? colors.brown : '#FFFDF066'}
        />
      </Stack>
    ))}
  </Stack>
)

const Index = () => {
  const [tab, setTab] = useState(LIST_TAB)
  const [isLoadLocal, setIsLoadLocal] = useState<boolean>(false)

  const [transactions, setTransactions] = useState<TransactionInterface[]>([])
  const [objType, setObjType] = useState<string>(TRANSACTION_CONTENT_ENUM.ALL)
  const [type, setType] = useState<string>(TRANSACTION_TYPE_ENUM.ALL)
  const scrollRef = useRef(false)
  const scrollTopRef = useRef<number>(0)

  const [page, setPage] = useState(1)

  const transactionTypeOptions = Object.values(TRANSACTION_TYPE_ENUM).map(
    (status) => ({
      value: status,
      label: getTransactionType(status),
    })
  )

  const transactionObjTypeOptions = Object.values(TRANSACTION_CONTENT_ENUM).map(
    (content) => ({
      value: content,
      label: getTransactionContent(content),
    })
  )

  const handleChangeStatus = (type: TRANSACTION_TYPE_ENUM) => {
    setType(type)
  }

  const handleChangeObjType = (objType: TRANSACTION_CONTENT_ENUM) => {
    setObjType(objType)
  }

  useEffect(() => {
    setPage(1)
    fetchTransactions(1, 10, null, null, type, objType, true)
  }, [type, objType])

  useEffect(() => {
    if (page !== 1) {
      fetchTransactions(page, 10, null, null, type, objType, false)
    }
  }, [page])

  const [fetchTransactions, isLoading] = useFetching(
    async (
      pageList: number,
      pageSize: number,
      periodFrom: Date,
      periodTo: Date,
      type: TRANSACTION_TYPE_ENUM,
      objType: TRANSACTION_CONTENT_ENUM,
      isType: boolean
    ) => {
      const response = await UserService.getTransactions(
        pageList,
        pageSize,
        periodFrom,
        periodTo,
        type,
        objType
      )
      if (response.status === 200) {
        setTimeout(() => {
          scrollRef.current = false
        }, 500)
        if (isType) {
          setTransactions(response.data.transactions)
        } else {
          setTransactions((tr: TransactionInterface[]) => {
            return [...tr, ...response.data.transactions]
          })
        }
      } else {
        setTransactions([])
      }
    }
  )

  useEffect(() => {
    if (isLoading) {
      setIsLoadLocal(true)
    } else {
      const timer = setTimeout(() => {
        setIsLoadLocal(false)
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [isLoading])

  if (tab === REPORT_TAB) {
    return (
      <Stack height="100%" width="80%" minHeight={0}>
        <TransCustom i18nKey="1219" variant="h1" color={colors.yellow} />
        <Tabs tab={tab} setTab={setTab} />
        <Report />
      </Stack>
    )
  }

  return (
    <Stack height="100%" width="80%" minHeight={0}>
      <TransCustom i18nKey="1219" variant="h1" color={colors.yellow} />
      <Tabs tab={tab} setTab={setTab} />
      <Stack
        direction="row"
        alignItems="center"
        gap="20px"
        marginTop="20px"
        flex="none">
        <Stack width="40%" gap="10px">
          <TransCustom i18nKey="1412" variant="body2" />
          <Select
            disabled={isLoading}
            value={type}
            list={transactionTypeOptions}
            onChange={(e: any) => handleChangeStatus(e.target.value)}
          />
        </Stack>
        <Stack width="40%" maxWidth="200px" gap="10px">
          <TransCustom i18nKey="1413" variant="body2" />
          <Select
            disabled={isLoading}
            value={objType}
            list={transactionObjTypeOptions}
            onChange={(e: any) => handleChangeObjType(e.target.value)}
          />
        </Stack>
      </Stack>
      <Stack direction="row" marginTop="40px" width="100%">
        <TransCustom i18nKey="1414" width="50%" variant="description" />
        <TransCustom
          i18nKey="1408"
          width="25%"
          variant="description"
          textAlign="center"
        />
        <TransCustom
          i18nKey="13561"
          width="25%"
          variant="description"
          textAlign="center"
          marginLeft="-5px"
        />
      </Stack>
      <Stack
        flex="auto"
        minHeight={0}
        marginTop="20px"
        width="100%"
        overflow="hidden"
        borderBottom="1px solid #FFFDF04D"
        borderTop="1px solid #FFFDF04D">
        {isLoadLocal ? (
          <Stack height="100%" padding="20px">
            <SimpleLoader isLoad={isLoadLocal} local={true} />
          </Stack>
        ) : transactions.length > 0 ? (
          <TransactionsList
            scrollTopRef={scrollTopRef}
            transactions={transactions}
            setPage={setPage}
            scrollRef={scrollRef}
          />
        ) : (
          <TransCustom i18nKey="1415" textAlign="center" marginTop="20px" />
        )}
      </Stack>
    </Stack>
  )
}

export default Index
